<template>
  <HrPageLayout
    title="Imprest History"
    subtitle="View imprest request history"
    icon="📜"
    :breadcrumb-items="[
      { label: 'Imprest' },
      { label: 'History' }
    ]"
  >
    <div class="imprest-history-content">
      <!-- Stats -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-file"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total Imprest Requests</p>
          <h2 class="hr-metric-value">{{ totalImprestRequests }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-money"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total Imprest Amount</p>
          <h2 class="hr-metric-value">{{ formatPrice(totalImprestAmount) }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Reconciled Imprest</p>
          <h2 class="hr-metric-value">{{ reconciledImprestCount }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Pending Requests</p>
          <h2 class="hr-metric-value">{{ pendingImprestCount }}</h2>
            </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body">
          <div class="hr-form-grid">
            <div class="hr-filter-group">
              <label class="hr-form-label">Date From</label>
              <input type="date" class="hr-input" v-model="filterDateFrom" @change="handleFiltersChanged">
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Date To</label>
              <input type="date" class="hr-input" v-model="filterDateTo" @change="handleFiltersChanged">
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Department</label>
              <select class="hr-select" v-model="filterDepartment" @change="handleFiltersChanged">
                <option value="">All Departments</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.name }}</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Status</label>
              <select class="hr-select" v-model="filterStatus" @change="handleFiltersChanged">
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="hr-chart-card">
        <div class="hr-chart-header hr-flex-between">
          <h5 class="hr-chart-title">Imprest History List</h5>
          <div class="hr-flex hr-gap-2">
            <div class="hr-search-wrap">
              <input type="text" class="hr-input" v-model="search" @input="debounceSearch" placeholder="Search...">
              <i class="bx bx-search-alt search-icon"></i>
            </div>
            <button @click="downloadReport" class="hr-btn hr-btn-primary" :disabled="loading">
              <i class="bx bx-download"></i> Download Report
            </button>
          </div>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-wrap">
            <div class="hr-spinner"></div>
            <p>Loading imprest records...</p>
          </div>
          <div v-else-if="errorMessage" class="hr-alert hr-alert-danger hr-flex hr-align-center">
            <i class="bx bx-error-circle hr-me-2"></i>
            {{ errorMessage }}
            <button type="button" class="hr-btn hr-btn-sm hr-btn-danger hr-ms-2" @click="fetchImprestHistory">Retry</button>
          </div>
          <div v-else-if="filteredImprestHistory.length === 0" class="hr-empty-state">
            <div class="hr-empty-icon"><i class="bx bx-file"></i></div>
            <p>No imprest records found. Try adjusting your filters.</p>
          </div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
                <tr>
                  <th>Imprest ID</th>
                  <th>Purpose</th>
                  <th>Department</th>
                  <th>Requested Amount</th>
                  <th>Actual Expense</th>
                  <th>Request Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in filteredImprestHistory" :key="item.id">
                  <td>{{ item.id }}</td>
                  <td>{{ item.purpose }}</td>
                  <td>{{ item.department?.name || 'N/A' }}</td>
                  <td>{{ formatPrice(item.requestedAmount) }}</td>
                  <td>{{ formatPrice(item.actualExpense || 0) }}</td>
                  <td>{{ formatDate(item.requestDate) }}</td>
                  <td>
                    <span :class="getStatusBadgeClass(item.status)">{{ formatStatus(item.status) }}</span>
                  </td>
                  <td>
                    <div class="hr-flex hr-gap-1">
                      <button @click="viewImprestDetails(item)" class="hr-btn hr-btn-sm hr-btn-info" title="View Details">
                        <i class="bx bx-show"></i>
                      </button>
                      <button v-if="item.status === 'APPROVED' && item.reconciled === 'NO'" @click="reconcileImprest(item.id)" class="hr-btn hr-btn-sm hr-btn-success" title="Reconcile">
                        <i class="bx bx-check-circle"></i>
                      </button>
                      <button v-if="item.reconciled === 'YES'" class="hr-btn hr-btn-sm hr-btn-secondary" disabled title="Already Reconciled">
                        <i class="bx bx-check-double"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="hr-pagination-wrap hr-flex-between hr-mt-3">
            <div v-if="totalItems > 0" class="hr-text-muted hr-small">
              Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalItems) }} of {{ totalItems }} entries
            </div>
            <div v-else>&nbsp;</div>
            <nav v-if="totalPages > 1" class="hr-pagination">
              <button type="button" class="hr-page-btn" @click="goToPage(currentPage - 1)" :disabled="currentPage === 1">
                <i class="bx bx-chevron-left"></i>
              </button>
              <template v-for="(page, idx) in visiblePages" :key="idx">
                <button v-if="page !== '...'" type="button" class="hr-page-btn" :class="{ active: currentPage === page }" @click="goToPage(page)">{{ page }}</button>
                <span v-else class="hr-page-ellipsis">...</span>
              </template>
              <button type="button" class="hr-page-btn" @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages">
                <i class="bx bx-chevron-right"></i>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Imprest Details Modal -->
    <div v-if="showDetailsModal && selectedImprest" class="hr-modal-overlay" @click.self="closeDetailsModal">
      <div class="hr-modal-dialog-lg">
        <div class="hr-modal-content">
          <div class="hr-modal-header hr-flex-between">
            <h5 class="hr-modal-title"><i class="bx bx-file hr-me-2"></i> Imprest Details</h5>
            <button type="button" class="hr-modal-close" @click="closeDetailsModal" aria-label="Close">×</button>
          </div>
          <div class="hr-modal-body">
            <div class="hr-detail-section hr-mb-3">
              <h6 class="hr-detail-section-title">Basic Information</h6>
              <div class="hr-detail-grid">
                <div class="hr-detail-item">
                  <span class="hr-form-hint">Imprest ID</span>
                  <strong>{{ selectedImprest.id }}</strong>
                </div>
                <div class="hr-detail-item">
                  <span class="hr-form-hint">Status</span>
                  <div>
                    <span :class="getStatusBadgeClass(selectedImprest.status)">{{ formatStatus(selectedImprest.status) }}</span>
                    <span v-if="selectedImprest.reconciled === 'YES'" class="hr-badge hr-badge-info hr-ms-2">Reconciled</span>
                  </div>
                </div>
                <div class="hr-detail-item">
                  <span class="hr-form-hint">Requested By</span>
                  <strong>{{ selectedImprest.requester?.name || 'N/A' }}</strong>
                </div>
                <div class="hr-detail-item">
                  <span class="hr-form-hint">Department</span>
                  <strong>{{ selectedImprest.department?.name || 'N/A' }}</strong>
                </div>
                <div class="hr-detail-item">
                  <span class="hr-form-hint">Request Date</span>
                  <strong>{{ formatDate(selectedImprest.requestDate) }}</strong>
                </div>
                <div class="hr-detail-item">
                  <span class="hr-form-hint">Expected Date</span>
                  <strong>{{ formatDate(selectedImprest.expectedDate) }}</strong>
                </div>
              </div>
            </div>

            <div class="hr-detail-section hr-mb-3">
              <h6 class="hr-detail-section-title">Purpose & Amount</h6>
              <div class="hr-detail-grid">
                <div class="hr-detail-item hr-col-full">
                  <span class="hr-form-hint">Purpose</span>
                  <strong>{{ selectedImprest.purpose }}</strong>
                </div>
                <div class="hr-detail-item">
                  <span class="hr-form-hint">Requested Amount</span>
                  <strong class="hr-text-primary">{{ formatPrice(selectedImprest.requestedAmount) }}</strong>
                </div>
                <div class="hr-detail-item">
                  <span class="hr-form-hint">Actual Expense</span>
                  <strong v-if="selectedImprest.actualExpense" class="hr-text-success">{{ formatPrice(selectedImprest.actualExpense) }}</strong>
                  <span v-else class="hr-text-muted">Not yet reconciled</span>
                </div>
                <div v-if="selectedImprest.notes" class="hr-detail-item hr-col-full">
                  <span class="hr-form-hint">Notes</span>
                  <p class="hr-mb-0">{{ selectedImprest.notes }}</p>
                </div>
              </div>
            </div>

            <div class="hr-detail-section hr-mb-3" v-if="selectedImprest.status !== 'PENDING'">
              <h6 class="hr-detail-section-title">Processing Details</h6>
              <div class="hr-detail-grid">
                <div v-if="selectedImprest.status === 'APPROVED'" class="hr-detail-item">
                  <span class="hr-form-hint">Approved By</span>
                  <strong>{{ selectedImprest.approverId || 'N/A' }}</strong>
                </div>
                <div v-if="selectedImprest.status === 'APPROVED'" class="hr-detail-item">
                  <span class="hr-form-hint">Approval Date</span>
                  <strong>{{ formatDateTime(selectedImprest.approvalDate) || 'N/A' }}</strong>
                </div>
                <div v-if="selectedImprest.status === 'REJECTED'" class="hr-detail-item">
                  <span class="hr-form-hint">Rejected By</span>
                  <strong>{{ selectedImprest.approverId || 'N/A' }}</strong>
                </div>
                <div v-if="selectedImprest.status === 'REJECTED'" class="hr-detail-item">
                  <span class="hr-form-hint">Rejection Date</span>
                  <strong>{{ formatDateTime(selectedImprest.rejectionDate) || 'N/A' }}</strong>
                </div>
                <div v-if="selectedImprest.status === 'REJECTED' && selectedImprest.rejectionReason" class="hr-detail-item hr-col-full">
                  <span class="hr-form-hint">Rejection Reason</span>
                  <p class="hr-mb-0 hr-text-danger">{{ selectedImprest.rejectionReason }}</p>
                </div>
              </div>
            </div>

            <div class="hr-detail-section hr-mb-3" v-if="selectedImprest.reconciled === 'YES'">
              <h6 class="hr-detail-section-title">Reconciliation Details</h6>
              <div class="hr-detail-grid">
                <div class="hr-detail-item">
                  <span class="hr-form-hint">Reconciled By</span>
                  <strong>{{ selectedImprest.reconciledBy || 'N/A' }}</strong>
                </div>
                <div class="hr-detail-item">
                  <span class="hr-form-hint">Reconciliation Date</span>
                  <strong>{{ formatDateTime(selectedImprest.reconciliationDate) || 'N/A' }}</strong>
                </div>
                <div v-if="selectedImprest.reconciliationDetails" class="hr-detail-item hr-col-full">
                  <span class="hr-form-hint">Expense Breakdown</span>
                  <div class="hr-pre-box hr-mt-2"><pre>{{ selectedImprest.reconciliationDetails }}</pre></div>
                </div>
              </div>
            </div>

            <div class="hr-detail-section" v-if="selectedImprest.attachments && selectedImprest.attachments.length > 0">
              <h6 class="hr-detail-section-title">Attachments</h6>
              <div class="hr-flex hr-flex-wrap hr-gap-2">
                <div v-for="(attachment, index) in selectedImprest.attachments" :key="index" class="hr-attachment-item">
                  <a :href="getPathUrl(attachment.url)" target="_blank" class="hr-flex hr-align-center">
                    <i class="bx bx-file-blank hr-me-1 hr-text-danger"></i> {{ attachment.name }}
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div class="hr-modal-footer hr-flex hr-gap-2 hr-justify-end">
            <button type="button" class="hr-btn hr-btn-secondary" @click="closeDetailsModal">Close</button>
            <button v-if="selectedImprest.status === 'APPROVED' && selectedImprest.reconciled === 'NO'" type="button" class="hr-btn hr-btn-success" @click="reconcileImprest(selectedImprest.id)">
              <i class="bx bx-check-circle hr-me-1"></i> Reconcile
            </button>
            <button v-if="selectedImprest.reconciled === 'YES'" type="button" class="hr-btn hr-btn-primary" @click="printImprestDetails">
              <i class="bx bx-printer hr-me-1"></i> Print
            </button>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import moment from "moment";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl } from "@/api";
import router from "@/router";

export default {
  name: 'ImprestHistory',
  components: { HrPageLayout },
  data() {
    return {
      // Search and Filter
      search: '',
      filterDateFrom: '',
      filterDateTo: '',
      filterDepartment: '',
      filterStatus: '',

      // Statistics
      totalImprestRequests: 0,
      totalImprestAmount: 0,
      reconciledImprestCount: 0,
      pendingImprestCount: 0,

      // Departments for filtering
      departments: [],

      // Imprest History Data
      imprestHistory: [],
      filteredImprestHistory: [], // To hold filtered data
      selectedImprest: null,
      showDetailsModal: false,
      currentUser: 'Demo User', // Replace with actual logged-in user
      apiResponse: null, // Store the entire API response

      // Pagination
      currentPage: 1,
      pageSize: 10,
      totalPages: 0,
      totalItems: 0,

      // Loading and Error Handling
      loading: false,
      errorMessage: '',

      // Search debounce
      searchTimeout: null
    }
  },
  computed: {
    // Calculate visible page numbers for pagination
    visiblePages() {
      const pages = [];
      const maxVisiblePages = 5;

      if (this.totalPages <= maxVisiblePages) {
        // Show all pages if total pages are less than or equal to maxVisiblePages
        for (let i = 1; i <= this.totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Always show first page
        pages.push(1);

        // Calculate middle pages
        let startPage = Math.max(2, this.currentPage - 1);
        let endPage = Math.min(this.totalPages - 1, this.currentPage + 1);

        // Adjust if we're at the beginning or end
        if (this.currentPage <= 2) {
          endPage = 4;
        } else if (this.currentPage >= this.totalPages - 1) {
          startPage = this.totalPages - 3;
        }

        // Add ellipsis if needed after first page
        if (startPage > 2) {
          pages.push('...');
        }

        // Add middle pages
        for (let i = startPage; i <= endPage; i++) {
          pages.push(i);
        }

        // Add ellipsis if needed before last page
        if (endPage < this.totalPages - 1) {
          pages.push('...');
        }

        // Always show last page
        pages.push(this.totalPages);
      }

      return pages;
    }
  },
  mounted() {
    // Single call to fetch all data in sequence
    this.initialLoad();
  },
  methods: {
    // Load data in sequence to prevent race conditions
    async initialLoad() {
      try {
        this.loading = true;
        this.errorMessage = '';

        // Fetch all imprest history data
        await this.fetchImprestHistory();

        this.loading = false;
      } catch (error) {
        console.error('Error during initial load:', error);
        this.errorMessage = 'Failed to load data. Please try again later.';
        this.loading = false;
      }
    },

    // API Calls
    async fetchImprestHistory() {
      try {
        this.loading = true;
        this.errorMessage = '';

        const data = new FormData();
        data.append("function", "getImprestHistory");
        data.append("page", this.currentPage);
        data.append("pageSize", this.pageSize);

        // Add filter parameters if they exist
        if (this.search) data.append("search", this.search);
        if (this.filterDepartment) data.append("departmentId", this.filterDepartment);
        if (this.filterStatus) data.append("status", this.filterStatus);
        if (this.filterDateFrom) data.append("fromDate", this.filterDateFrom);
        if (this.filterDateTo) data.append("toDate", this.filterDateTo);

        const response = await execute(data, authUrl);

        // Store the entire response for statistics processing
        this.apiResponse = response.data;

        if (response.data.success) {
          // Get the imprest history array from the response
          const imprestData = response.data.data.imprestHistory || [];

          // Map the data to match our component's expected format
          this.imprestHistory = imprestData.map(item => ({
            id: item.request_id,
            purpose: item.purpose,
            department: {
              id: item.department_id,
              name: item.department
            },
            requestedAmount: parseFloat(item.amount),
            actualExpense: item.actual_expense ? parseFloat(item.actual_expense) : null,
            requestDate: item.request_date,
            expectedDate: item.expected_date,
            status: item.status.toUpperCase(),
            reconciled: item.reconciled,
            requester: {
              id: item.requester_id,
              name: item.names
            },
            notes: item.notes,
            approverId: item.approver_id,
            approvalDate: item.approval_date,
            rejectionReason: item.rejection_reason,
            rejectionDate: item.rejection_date,
            reconciledBy: item.reconciled_by,
            reconciliationDate: item.reconciliation_date,
            reconciliationDetails: item.reconciliation_details,
            attachments: item.attachments ? JSON.parse(item.attachments).map(path => ({
              url: path,
              name: path.split('/').pop()
            })) : []
          }));

          // Set filteredImprestHistory to the mapped data
          this.filteredImprestHistory = [...this.imprestHistory];

          // Get pagination details from response if available
          if (response.data.data.pagination) {
            this.totalItems = response.data.data.pagination.total || imprestData.length;
            this.totalPages = response.data.data.pagination.pages || Math.ceil(this.totalItems / this.pageSize);
          } else {
            // Fallback to client-side pagination if server doesn't provide it
            this.totalItems = imprestData.length;
            this.totalPages = Math.ceil(this.totalItems / this.pageSize);
          }

          // Process statistics after data is loaded
          this.processStatistics();

          // Extract unique departments for filter dropdown
          this.extractDepartments(imprestData);
        } else {
          console.error("Error fetching imprest history:", response.data.message);
          this.errorMessage = response.data.message || 'Failed to load imprest history. Please try again later.';
          this.imprestHistory = [];
          this.filteredImprestHistory = [];
        }
      } catch (error) {
        console.error('Error fetching imprest history:', error);
        this.errorMessage = 'Failed to load imprest history. Please try again later.';
        this.imprestHistory = [];
        this.filteredImprestHistory = [];
      } finally {
        this.loading = false;
      }
    },


    getPathUrl(path) {
      return authUrl+''+path;
    },

    // Extract departments from imprest data
    extractDepartments(imprestData) {
      const uniqueDepartments = [];
      const departmentIds = new Set();

      imprestData.forEach(item => {
        if (item.department_id && !departmentIds.has(item.department_id)) {
          departmentIds.add(item.department_id);
          uniqueDepartments.push({
            id: item.department_id,
            name: item.department
          });
        }
      });

      this.departments = uniqueDepartments;
    },

    // Process statistics from imprest history data
    processStatistics() {
      try {
        // Get metrics from the API response if available
        if (this.apiResponse && this.apiResponse.data && this.apiResponse.data.metrics) {
          const metrics = this.apiResponse.data.metrics;

          // Set the statistics from the metrics object
          this.totalImprestRequests = parseInt(metrics.totalRequests || 0);
          this.totalImprestAmount = parseFloat(metrics.totalAmount || 0);
          this.reconciledImprestCount = parseInt(metrics.reconciled || 0);
          this.pendingImprestCount = parseInt(metrics.pending || 0);
        } else {
          // Calculate from all imprest records (not just current page)
          const allImprestData = this.apiResponse?.data?.data?.imprestHistory || [];

          // Set total requests count
          this.totalImprestRequests = allImprestData.length;

          // Calculate total amount
          this.totalImprestAmount = allImprestData.reduce((total, item) => {
            return total + (parseFloat(item.amount) || 0);
          }, 0);

          // Count reconciled imprest requests
          this.reconciledImprestCount = allImprestData.filter(item =>
              item.reconciled === 'YES'
          ).length;

          // Count pending requests
          this.pendingImprestCount = allImprestData.filter(item =>
              item.status.toUpperCase() === 'PENDING'
          ).length;
        }
      } catch (error) {
        console.error('Error processing statistics:', error);
        // Set default values if calculation fails
        this.totalImprestRequests = 0;
        this.totalImprestAmount = 0;
        this.reconciledImprestCount = 0;
        this.pendingImprestCount = 0;
      }
    },

    formatStatus(status) {
      // Capitalize the first letter and lowercase the rest
      return status.charAt(0) + status.slice(1).toLowerCase();
    },

    async downloadReport() {
      try {
        this.loading = true;

        const data = new FormData();
        data.append("function", "generateImprestReport");
        data.append("reportType", "PDF");

        // Add filter parameters if they exist
        if (this.search) data.append("search", this.search);
        if (this.filterDepartment) data.append("departmentId", this.filterDepartment);
        if (this.filterStatus) data.append("status", this.filterStatus);
        if (this.filterDateFrom) data.append("fromDate", this.filterDateFrom);
        if (this.filterDateTo) data.append("toDate", this.filterDateTo);

        const response = await execute(data, authUrl, { responseType: 'blob' });

        if (response.data) {
          // Create blob link to download
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `Imprest_Report_${new Date().toISOString().split('T')[0]}.pdf`);
          document.body.appendChild(link);
          link.click();

          // Clean up and remove the link
          link.parentNode.removeChild(link);
        } else {
          console.error('Error downloading report: Empty response');
          this.$toast.error('Failed to download report. Please try again later.');
        }
      } catch (error) {
        console.error('Error downloading report:', error);
        this.$toast.error('Failed to download report. Please try again later.');
      } finally {
        this.loading = false;
      }
    },

    async reconcileImprest(imprestId) {

        const confirmAction = confirm('Are you sure you want to reconcile this imprest?');
        if (!confirmAction) return;
        sessionStorage.setItem('imprestId', imprestId);
        await router.push('imprest-reconciliation')


    },

    viewImprestDetails(item) {
      this.selectedImprest = item;
      this.showDetailsModal = true;
    },
    closeDetailsModal() {
      this.showDetailsModal = false;
    },

    // Utility Methods
    formatPrice(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value);
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return moment(dateString).format('DD MMM YYYY');
    },

    formatDateTime(dateString) {
      if (!dateString) return '';
      return moment(dateString).format('DD MMM YYYY HH:mm');
    },

    getStatusBadgeClass(status) {
      const statusClasses = {
        'PENDING': 'hr-badge hr-badge-warning',
        'APPROVED': 'hr-badge hr-badge-success',
        'REJECTED': 'hr-badge hr-badge-danger',
        'RECONCILED': 'hr-badge hr-badge-info'
      };
      return statusClasses[status] || 'hr-badge hr-badge-secondary';
    },

    printImprestDetails() {
      // Open a new window for printing
      const printWindow = window.open('', '_blank');

      // Generate print content
      let printContent = `
        <html>
        <head>
          <title>Imprest Details - ${this.selectedImprest.id}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .header h2 { margin-bottom: 5px; }
            .section { margin-bottom: 20px; }
            .section-title { background-color: #f0f0f0; padding: 5px 10px; font-weight: bold; margin-bottom: 10px; }
            .row { display: flex; margin-bottom: 10px; }
            .col { flex: 1; padding-right: 15px; }
            .label { font-weight: bold; margin-bottom: 5px; }
            .value { margin-bottom: 5px; }
            .badge { display: inline-block; padding: 3px 8px; border-radius: 3px; font-size: 12px; }
            .badge-success { background-color: #28a745; color: white; }
            .badge-warning { background-color: #ffc107; color: #212529; }
            .badge-danger { background-color: #dc3545; color: white; }
            .badge-info { background-color: #17a2b8; color: white; }
            .pre-box { background-color: #f8f9fa; padding: 10px; white-space: pre-wrap; }
            table { width: 100%; border-collapse: collapse; }
            table, th, td { border: 1px solid #ddd; }
            th, td { padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>Imprest Details</h2>
            <p>Reference: ${this.selectedImprest.id}</p>
          </div>

          <div class="section">
            <div class="section-title">Basic Information</div>
            <div class="row">
              <div class="col">
                <div class="label">Status</div>
                <div class="value">
                  <span class="badge badge-${this.getStatusClass(this.selectedImprest.status)}">
                    ${this.formatStatus(this.selectedImprest.status)}
                  </span>
                  ${this.selectedImprest.reconciled === 'YES' ? '<span class="badge badge-info">Reconciled</span>' : ''}
                </div>
              </div>
              <div class="col">
                <div class="label">Requested By</div>
                <div class="value">${this.selectedImprest.requester?.name || 'N/A'}</div>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <div class="label">Department</div>
                <div class="value">${this.selectedImprest.department?.name || 'N/A'}</div>
              </div>
              <div class="col">
                <div class="label">Request Date</div>
                <div class="value">${this.formatDate(this.selectedImprest.requestDate)}</div>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Purpose & Amount</div>
            <div class="row">
              <div class="col">
                <div class="label">Purpose</div>
                <div class="value">${this.selectedImprest.purpose}</div>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <div class="label">Requested Amount</div>
                <div class="value">${this.formatPrice(this.selectedImprest.requestedAmount)}</div>
              </div>
              <div class="col">
                <div class="label">Actual Expense</div>
                <div class="value">${this.selectedImprest.actualExpense ? this.formatPrice(this.selectedImprest.actualExpense) : 'Not reconciled'}</div>
              </div>
            </div>
            ${this.selectedImprest.notes ? `
            <div class="row">
              <div class="col">
                <div class="label">Notes</div>
                <div class="value">${this.selectedImprest.notes}</div>
              </div>
            </div>
            ` : ''}
          </div>
      `;

      // Add reconciliation details if reconciled
      if (this.selectedImprest.reconciled === 'YES') {
        printContent += `
          <div class="section">
            <div class="section-title">Reconciliation Details</div>
            <div class="row">
              <div class="col">
                <div class="label">Reconciled By</div>
                <div class="value">${this.selectedImprest.reconciledBy || 'N/A'}</div>
              </div>
              <div class="col">
                <div class="label">Reconciliation Date</div>
                <div class="value">${this.formatDateTime(this.selectedImprest.reconciliationDate) || 'N/A'}</div>
              </div>
            </div>
            ${this.selectedImprest.reconciliationDetails ? `
            <div class="row">
              <div class="col">
                <div class="label">Expense Breakdown</div>
                <div class="pre-box">${this.selectedImprest.reconciliationDetails}</div>
              </div>
            </div>
            ` : ''}
          </div>
        `;
      }

      // Close the HTML document
      printContent += `
          <div class="section">
            <div class="section-title">Authentication</div>
            <p>Printed on: ${new Date().toLocaleString()}</p>
            <p>Printed by: ${this.currentUser || 'System User'}</p>
          </div>
        </body>
        </html>
      `;

      // Write to the new window and print
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();

      // Wait for content to load then print
      printWindow.onload = function() {
        printWindow.print();
      };
    },

    getStatusClass(status) {
      const statusClasses = {
        'PENDING': 'warning',
        'APPROVED': 'success',
        'REJECTED': 'danger',
        'RECONCILED': 'info'
      };
      return statusClasses[status] || 'secondary';
    },

    // Pagination
    goToPage(page) {
      if (page === '...') return;

      this.currentPage = page;
      this.fetchImprestHistory();
    },

    // Debounce search to avoid too many API calls
    debounceSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1; // Reset to first page when searching
        this.fetchImprestHistory();
      }, 500);
    },

    // Handle filter changes
    handleFiltersChanged() {
      this.currentPage = 1; // Reset to first page when filters change
      this.fetchImprestHistory();
    }
  }
}
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.imprest-history-content { padding: 0; }

/* Modal overlay */
.hr-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}
.hr-modal-dialog-lg {
  max-width: 720px;
  width: 95%;
  max-height: 90vh;
  overflow-y: auto;
}
.hr-modal-content {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}
.hr-modal-header {
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.hr-modal-title { margin: 0; font-size: 1.1rem; display: flex; align-items: center; }
.hr-modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #64748b;
  line-height: 1;
}
.hr-modal-body { padding: 20px; max-height: 60vh; overflow-y: auto; }
.hr-modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
}
.hr-detail-section { margin-bottom: 1rem; }
.hr-detail-section-title {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 8px;
  padding-bottom: 4px;
  color: #475569;
  border-bottom: 1px solid #e2e8f0;
}
.hr-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px 24px;
}
.hr-detail-item { display: flex; flex-direction: column; gap: 2px; }
.hr-detail-item.hr-col-full { grid-column: 1 / -1; }
.hr-pre-box { background: #f8fafc; padding: 12px; border-radius: 8px; white-space: pre-wrap; }
.hr-pre-box pre { margin: 0; font-size: 0.875rem; }
.hr-attachment-item {
  border: 1px solid #e2e8f0;
  padding: 8px 12px;
  border-radius: 8px;
}
.hr-attachment-item a { text-decoration: none; color: inherit; }
.hr-attachment-item a:hover { color: #4f46e5; }
.hr-page-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  padding: 0 4px;
  color: #64748b;
  font-size: 14px;
}
</style>